import { useMemo, type FC } from 'react'
import clsx from 'clsx'

import type { BannerProps } from '@wartek-id/banner-v1'
import { Banner, BannerAction, BannerDescription } from '@wartek-id/banner-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Link } from '@wartek-id/link'
import { Text } from '@wartek-id/text-v1'

import { formatDateTimeWithTimezone } from 'utils/datetime'
import { useApiGetPengaliranSkpStatus } from 'api/kinerja/pengaliran-data-ekin'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { TRequestStatusPengaliranSkp } from 'api/kinerja/pengaliran-data-ekin'

type BannerPengaliranEkinProps = {
  jabatan: TRequestStatusPengaliranSkp['jabatan']
}

type TContent = Partial<BannerProps> & {
  show: boolean
  title?: string
  description?: string | React.ReactNode
  actionLabel?: string
}

const BannerPengaliranEkin: FC<BannerPengaliranEkinProps> = ({ jabatan }) => {
  const { isEnabled, isFetched, periodYear } = usePeriodContext()

  const {
    data: { data } = {},
    isFetching,
    isSuccess,
  } = useApiGetPengaliranSkpStatus(
    { year: periodYear, jabatan },
    { enabled: isEnabled && isFetched }
  )

  const content: TContent = useMemo(() => {
    let res: TContent = { show: false }

    // If the data is not successfully fetched, or still fetching, return the default value
    if (!isSuccess || isFetching) return res

    // If the user is not ASN, return the default value
    if (!data.asn) return res

    const synced =
      data?.pengaliran && 'synced' in data.pengaliran
        ? data.pengaliran.synced
        : undefined

    // If the synced is undefined, return the default value
    if (synced === undefined) return res

    const lastSync =
      data?.pengaliran && 'lastSync' in data.pengaliran
        ? data.pengaliran.lastSync
        : undefined

    switch (synced) {
      case true:
        res = {
          actionLabel: 'Ke E-Kinerja',
          className: '!bg-green-5 !border-green-50',
          customIcon: (
            <Icon color="default" fontSize="default">
              check_circle_outline
            </Icon>
          ),
          description: (
            <div>
              <Text color="subdued">
                Kunjungi E-Kinerja untuk cek data. Jika ada perbedaan, tekan
                tombol "Sinkro PMM" di E-Kinerja dan ikuti petunjuk yang
                tersedia.
              </Text>

              {/* This information will only be shown if the data is successfully fetched, and `synced` set to true */}
              {synced && (
                <>
                  <br />
                  <Text color="subdued">
                    Pengaliran terakhir dilakukan pada:{' '}
                    {formatDateTimeWithTimezone({ dateTime: lastSync })}
                  </Text>
                </>
              )}
            </div>
          ),
          show: true,
          title:
            'Data Sasaran Kinerja Pegawai (SKP) sudah dialirkan ke E-Kinerja',
          type: 'informational',
        }
        break

      case false:
        res = {
          actionLabel: 'Ke E-Kinerja',
          className: '!bg-blue-5 !border-blue-70',
          customIcon: (
            <Icon
              className="banner-title-icon !mr-3"
              color="default"
              fontSize="default"
            >
              hourglass_empty
            </Icon>
          ),
          description:
            'Kunjungi E-Kinerja untuk mengalirkan data. Tekan tombol "Sinkro PMM" di E-Kinerja dan ikuti petunjuk yang tersedia.',
          show: true,
          title:
            'Data Sasaran Kinerja Pegawai (SKP) belum dialirkan ke E-Kinerja',
          type: 'informational',
        }
        break
    }

    return res
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (isFetching || !content.show) return null

  return (
    <>
      <Banner
        closable={false}
        customIcon={content?.customIcon}
        title={content?.title}
        type={content?.type}
        className={clsx(
          '!p-3',
          '[&>div:nth-child(1)>div:nth-child(1):before]:hidden', // Hide predefined icon
          '[&>div:nth-child(1)>div]:space-x-3', // Extra spacing between icon and title
          '[&>div:nth-child(1)>div>span[role=img]]:m-0', // Remove extra margin on icon
          '[&>div:nth-child(2)]:mt-1', // Override margin between title and description
          '[&>div:nth-child(2)]:pl-9', // Override padding for description
          '[&>div:nth-child(2)>div:nth-child(2)]:mt-1', // Override margin between description and action
          content?.className
        )}
      >
        {content?.description && (
          <BannerDescription>{content?.description}</BannerDescription>
        )}

        {content?.actionLabel && (
          <BannerAction>
            <Link
              href="https://kinerja.bkn.go.id/skp"
              target="_blank"
              variant="primary"
              title={content?.actionLabel}
              className="!no-underline hover:!underline !p-0 focus:!shadow-none !text-blue-70 font-bold"
            >
              {content?.actionLabel}
            </Link>
          </BannerAction>
        )}
      </Banner>
    </>
  )
}

export default BannerPengaliranEkin
